import { useEffect, useRef, useState } from 'react'
import type { ExtractedItem } from '../lib/api'

/**
 * PreviewCard — read-only prose preview of the report being assembled.
 *
 * Three sections (2026-05-06 condensed):
 *   1. ITEM DETAILS  │ Brand · Model · Color · Condition
 *      Title + tokens on a single row, separated by a vertical
 *      hairline (Augusta-style edge structure). As each field gets
 *      captured, its token flips from faded grey to soft Augusta
 *      green. Below: a flowing prose sentence (or empty space).
 *   2. LOCATION & DATE │ Venue · City · State · Date
 *      Same single-row pattern. Below: the prose location line.
 *   3. ADDITIONAL INFORMATION
 *      Just the title (no tokens); the prose distinguishing-marks
 *      sentence appears below once captured.
 *
 * Per Ryan (2026-05-06): "Lets do these all on one line to make
 * more space... still keep the full boxes roughly the same size,
 * let's just make things feel not so scrunched and let them
 * breathe." Single-row headers + larger vertical padding give the
 * prose breathing room while preserving the box rhythm. Size and
 * Time tokens removed from the legend (still extracted server-side,
 * still surface in the prose sentence — just not as token chips).
 */

interface PreviewCardProps {
  item: Partial<ExtractedItem>
}

const JUNK_VALUES = new Set([
  '<unknown>',
  'unknown',
  'none',
  'n/a',
  'na',
  'null',
  'undefined',
])

function valueFor(item: Partial<ExtractedItem>, key: keyof ExtractedItem): string | undefined {
  const v = item[key]
  if (typeof v !== 'string') return undefined
  const t = v.trim()
  if (!t.length) return undefined
  if (JUNK_VALUES.has(t.toLowerCase())) return undefined
  if (key === 'date_occurred' && /^\d{4}-\d{2}-\d{2}$/.test(t)) {
    const [y, m, d] = t.split('-').map(Number)
    const dt = new Date(Date.UTC(y, m - 1, d))
    if (!isNaN(dt.getTime())) {
      return dt.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC',
      })
    }
  }
  return t
}

// ── Prose helpers — snake_case → spaces; common nouns lower, proper nouns asis.

function unsnake(s: string): string {
  return s.replace(/_/g, ' ').trim()
}
function lowerNoun(s: string | undefined): string {
  if (!s) return ''
  return unsnake(s).toLowerCase()
}
function asIs(s: string | undefined): string {
  if (!s) return ''
  return unsnake(s)
}

function buildItemSentence(item: Partial<ExtractedItem>): string | null {
  const color = lowerNoun(valueFor(item, 'color_primary'))
  // Strip leading "the " from brand so we don't get "A black The North
  // Face Nano Puff" when the brand is canonically "The North Face." The
  // determiner reads as ungrammatical inside the sentence head; the
  // brand on its own ("North Face") is what the prose needs.
  const brandRaw = asIs(valueFor(item, 'brand'))
  const brand = brandRaw.replace(/^the\s+/i, '')
  const model = asIs(typeof item.model === 'string' ? item.model : undefined)
  const sub = lowerNoun(valueFor(item, 'subcategory'))
  const cat = lowerNoun(valueFor(item, 'category'))
  const material = lowerNoun(valueFor(item, 'material'))
  const condition = lowerNoun(valueFor(item, 'condition'))
  const size = asIs(typeof item.size === 'string' ? item.size : undefined)

  const noun = sub || cat
  const desc = [color, brand, model].filter(Boolean).join(' ')
  const head = noun
    ? desc
      ? `A ${desc} ${noun}`
      : `A ${noun}`
    : desc
      ? `A ${desc}`
      : ''

  const tail: string[] = []
  if (size) tail.push(`size ${size}`)
  if (material) tail.push(material)
  if (condition) tail.push(`${condition} condition`)

  let sentence = head
  if (tail.length > 0) sentence += sentence ? ` — ${tail.join(', ')}` : tail.join(', ')

  return sentence ? sentence + '.' : null
}

function buildLocationSentence(item: Partial<ExtractedItem>): string | null {
  const venue = valueFor(item, 'venue_name')
  const city = valueFor(item, 'venue_city')
  const state = valueFor(item, 'venue_state')
  const cityState = [city, state].filter(Boolean).join(', ')
  const date = valueFor(item, 'date_occurred')
  // Strip a leading "around " from time_approximate so we don't get
  // "around around 2pm" when the user says "around 2pm" and the LLM
  // stores that verbatim as the field value. The "around" prefix
  // belongs in the sentence template, not the data.
  const time = typeof item.time_approximate === 'string'
    ? item.time_approximate.trim().toLowerCase().replace(/^around\s+/, '')
    : ''

  const parts: string[] = []
  if (date) parts.push(date)
  if (time) parts.push(`around ${time}`)
  if (venue) parts.push(`at ${venue}`)
  if (cityState) parts.push(`in ${cityState}`)

  return parts.length > 0 ? parts.join(', ') + '.' : null
}

function buildAdditionalSentence(item: Partial<ExtractedItem>): string | null {
  const marks = valueFor(item, 'distinguishing_marks')
  const notes = valueFor(item, 'notes')
  const summary = [marks, notes].filter(Boolean).join('. ')
  if (!summary) return null
  return summary + (summary.endsWith('.') ? '' : '.')
}

// ── Conversation prompts — a coach above the card ──
//
// Per Ryan: keep the prompts simple and item-focused. Drop the cute
// story-oriented and category-specific pools — they read as flair when
// what we need is structured info (date, time, venue, where exactly,
// color, brand, distinguishing details). One small pool, plain text.

const SIMPLE_PROMPTS = [
  'Was it today?',
  'What time, roughly?',
  'What was the venue?',
  'Where in the venue exactly?',
  'What color?',
  'Any brand?',
  'Anything written on it?',
  'Was it new or worn?',
  'Roughly what size?',
  'Anything special about it?',
] as const

function pickConversationPrompts(_category: string | undefined): string[] {
  // Single simple pool — same prompts regardless of category. Per Ryan:
  // "you're getting too cute, keep it on the info we need."
  return [...SIMPLE_PROMPTS]
}

export default function PreviewCard({ item }: PreviewCardProps) {
  const itemSentence = buildItemSentence(item)
  const locationSentence = buildLocationSentence(item)
  const additionalSentence = buildAdditionalSentence(item)

  // Each token's tooltip ("why we ask") teaches without lecturing — long-
  // press / hover surfaces the rationale natively via the title attr.
  // Size and Time removed from the legend (per Ryan 2026-05-06): we
  // still extract them, they still appear in the prose sentence, but
  // showing them as token chips here was making the row too dense.
  const itemTokens: TokenDef[] = [
    { label: 'Brand',     filled: !!valueFor(item, 'brand'),
      tooltip: 'Brand narrows the search significantly when venues catalog finds.' },
    { label: 'Model',     filled: typeof item.model === 'string' && item.model.trim().length > 0,
      tooltip: 'Model (e.g. "Nano Puff") is often what staff write on the lost-and-found tag.' },
    { label: 'Color',     filled: !!valueFor(item, 'color_primary'),
      tooltip: 'Color is the first thing staff sort by — get this right and matching speeds up.' },
    { label: 'Condition', filled: !!valueFor(item, 'condition'),
      tooltip: 'New vs. well-worn helps tell apart similar items from different owners.' },
  ]

  const locationTokens: TokenDef[] = [
    { label: 'Venue', filled: !!valueFor(item, 'venue_name'),
      tooltip: 'We route your report to this venue — without it, no staff to check with.' },
    { label: 'City',  filled: !!valueFor(item, 'venue_city'),
      tooltip: 'City helps when a chain venue has multiple locations.' },
    { label: 'State', filled: !!valueFor(item, 'venue_state'),
      tooltip: 'State helps when a chain venue has multiple locations.' },
    { label: 'Date',  filled: !!valueFor(item, 'date_occurred'),
      tooltip: 'We use this to narrow your match window — venues clear lost-and-found weekly.' },
  ]

  // Live "detail count" — total filled fields across both token rows
  // PLUS the additional info text. Quiet quantitative signal that
  // "the more I share, the more I have to recover with."
  const filledCount =
    itemTokens.filter((t) => t.filled).length +
    locationTokens.filter((t) => t.filled).length +
    (additionalSentence ? 1 : 0)
  const totalSlots = itemTokens.length + locationTokens.length + 1

  return (
    <div
      className="w-full max-w-md mx-auto border bg-[var(--surface-1)]"
      style={{
        borderColor: 'var(--border-subtle)',
        borderRadius: 0,
      }}
    >
      {/* Detail count at the TOP — only renders once at least one
          field has been extracted. Per Ryan: "don't start showing 0/11
          have it pop up after the first message comes back." Pops in
          on the first agent response that lands a detail. */}
      {filledCount > 0 && (
        <p
          className="px-4 py-1.5 text-center border-b animate-fade-in"
          style={{
            borderColor: 'var(--border-subtle)',
            color: 'var(--ink-4)',
            fontFamily: 'var(--font-sans)',
            // Round 9: tokenized. Was inline 11px/500/0.22em.
            // Counter is intentionally smaller (--eyebrow-size-sm) and
            // lighter weight than canonical brand eyebrows so it reads
            // as program-note chrome, not the typographic king.
            fontSize: 'var(--eyebrow-size-sm)',
            letterSpacing: 'var(--eyebrow-tracking)',
            textTransform: 'uppercase',
            fontWeight: 500,
            margin: 0,
          }}
          aria-live="polite"
        >
          <span style={{ color: 'var(--accent)' }}>{filledCount}</span>
          <span> / {totalSlots} details captured</span>
        </p>
      )}
      <Section
        title="Item Details"
        tokens={itemTokens}
        sentence={itemSentence}
      />
      <Section
        title="Location & Date"
        tokens={locationTokens}
        sentence={locationSentence}
        topBorder
      />
      <AdditionalInfoSection sentence={additionalSentence} />
    </div>
  )
}

// ── ConversationCoach — plain rotating prompt above the card.
//
// Per Ryan: lift it out of the bordered card and let it just be text
// floating above. Simple item-focused questions only.
//
// Exported so Home renders it as a sibling above PreviewCard, not
// nested inside the card's border.

export function ConversationCoach({ item }: { item: Partial<ExtractedItem> }) {
  const category = typeof item.category === 'string' ? item.category : undefined
  const subcategory = typeof item.subcategory === 'string' ? item.subcategory : undefined
  const prompts = pickConversationPrompts(subcategory ?? category)
  const [idx, setIdx] = useState(0)

  // Reset index when prompt set changes (category resolves → new pool).
  useEffect(() => {
    setIdx(0)
  }, [prompts.join('|')])

  // Slow rotation — 5.5s — so the user has time to actually read each
  // prompt and consider their answer.
  useEffect(() => {
    if (prompts.length <= 1) return
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % prompts.length)
    }, 5500)
    return () => clearInterval(id)
  }, [prompts.length])

  return (
    <div
      className="w-full max-w-md mx-auto flex items-center justify-center px-4"
      style={{ minHeight: '2rem' }}
    >
      <p
        key={idx + ':' + prompts[idx]}
        className="leading-snug italic animate-fade-in text-center"
        style={{
          color: 'var(--ink-3)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-caption)',
          margin: 0,
        }}
        aria-live="polite"
      >
        {prompts[idx]}
      </p>
    </div>
  )
}

// ── Section with token-highlighted legend ──

interface TokenDef {
  label: string
  filled: boolean
  /** Optional tooltip — surfaced via native title attribute on the
   *  token. Per Ryan opp 8: "why we ask" educators on hover. */
  tooltip?: string
}

// Designer pass 2026-05-06, mobile-tight revision per Ryan: "the
// optimization for the spacing and these kind of things needs to be
// for mobile devices. there's too much padding between Item Details
// and the top of the box and location and date and the line."
//
// 2026-05-06b — desktop scale-up per Ryan: "The item details box
// needs to be 20% taller with 20% bigger font." Box min-height
// 6rem → 7.2rem; token-row + prose 13px → 16px; eyebrow + counter
// 11px → 13px. Hairline divider stays at 0.95em (em-relative, so
// it scales automatically with the new font sizes).
//
//   - Min-height 7.2rem.
//   - Section padding pt-3/pb-3/px-4 (12px vertical, 16px horizontal).
//   - Header row gap-x-3.
//   - Hairline divider 0.95em (em-relative — scales with font).
//   - Prose value 16px Inter --ink-1 (matches the token row).
//   - Top counter padding py-1.5, tracking 0.22em.
const SECTION_MIN_HEIGHT = '7.2rem'
const ADDITIONAL_MIN_HEIGHT = SECTION_MIN_HEIGHT

function Section({
  title,
  tokens,
  sentence,
  topBorder = false,
}: {
  title: string
  tokens: TokenDef[]
  sentence: string | null
  topBorder?: boolean
}) {
  const filled = !!sentence
  // Track previous filled state per token so we can fire a one-shot
  // celebration pulse the moment a label transitions grey → green.
  const prevFilledRef = useRef<Record<string, boolean>>({})
  const [pulsing, setPulsing] = useState<Record<string, boolean>>({})
  useEffect(() => {
    const next: Record<string, boolean> = {}
    for (const t of tokens) {
      if (t.filled && !prevFilledRef.current[t.label]) next[t.label] = true
    }
    if (Object.keys(next).length > 0) {
      setPulsing((p) => ({ ...p, ...next }))
      // Clear after the animation duration so the keyframe can re-fire
      // if the user clears + re-fills the same field.
      const t = setTimeout(() => {
        setPulsing((p) => {
          const cleared = { ...p }
          for (const k of Object.keys(next)) delete cleared[k]
          return cleared
        })
      }, 600)
      return () => clearTimeout(t)
    }
    // Always sync the previous map so future fills are detected.
    prevFilledRef.current = Object.fromEntries(tokens.map((t) => [t.label, t.filled]))
  }, [tokens])
  return (
    <div
      className={`px-4 pt-3 pb-3 ${topBorder ? 'border-t' : ''}`}
      style={{ borderColor: 'var(--border-subtle)', minHeight: SECTION_MIN_HEIGHT }}
    >
      {/* Header row — title + tokens on one line, separated by an
          Augusta-style vertical hairline. flex-wrap so narrow phones
          can break gracefully (title above, tokens below) without
          overflow. items-baseline keeps the eyebrow + tokens aligned
          to the same text baseline despite different font sizes. */}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
        <span
          className="uppercase shrink-0"
          style={{
            color: 'var(--accent)',
            fontSize: 'var(--eyebrow-size)',
            fontWeight: 'var(--eyebrow-weight)',
            letterSpacing: 'var(--eyebrow-tracking)',
          }}
        >
          {title}
        </span>
        {/* Vertical hairline — uses an inline-block sized to match
            the cap height of the tokens. shrink-0 prevents it
            collapsing when the row gets tight. */}
        <span
          aria-hidden
          className="hidden sm:inline-block shrink-0"
          style={{
            width: '1px',
            height: '0.95em',
            background: 'var(--border-default)',
            transform: 'translateY(1px)',
          }}
        />
        <span
          className="text-[16px]"
          style={{
            fontFamily: 'var(--font-sans)',
            letterSpacing: '0.01em',
            lineHeight: 1.45,
          }}
        >
          {tokens.map((t, i) => (
            <span key={t.label}>
              <span
                title={t.tooltip}
                style={{
                  // Filled tokens use --accent-soft (lighter green) at
                  // weight 500 — visible but not shouty, so the grey
                  // unfilled tokens still feel prevalent. Per Ryan:
                  // "they become a very light green, to make the
                  // remaining gray more prevalent."
                  color: t.filled ? 'var(--accent-soft)' : 'var(--ink-3)',
                  fontWeight: t.filled ? 500 : 400,
                  transition: 'color 0.4s ease, font-weight 0.4s ease',
                  display: 'inline-block',
                  animation: pulsing[t.label] ? 'token-fill-pulse 600ms ease-out' : undefined,
                  cursor: t.tooltip ? 'help' : undefined,
                }}
              >
                {t.label}
              </span>
              {i < tokens.length - 1 && (
                <span style={{ color: 'var(--ink-4)' }}> · </span>
              )}
            </span>
          ))}
        </span>
      </div>
      {/* Prose value — matches the token row size + font, just
          darker. Per Ryan 2026-05-06: "the text of the things that
          get filled out in the item details, it needs to be the
          same size and font as the 'brand,model' etc but it just
          needs to be the darker color." 16px Inter --ink-1 (was
          13px — scaled +20% with the rest of the box).
          Deliberately blank when empty — that whitespace IS the
          breathing room. */}
      {filled && (
        <p
          className="text-[16px]"
          style={{
            fontFamily: 'var(--font-sans)',
            color: 'var(--ink-1)',
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: '0.01em',
            margin: 0,
          }}
        >
          {sentence}
        </p>
      )}
    </div>
  )
}

// ── Additional Information — category-aware rotating prompts ──

function AdditionalInfoSection({ sentence }: { sentence: string | null }) {
  // Section stays empty until the user shares distinguishing marks /
  // notes. The "More detail helps us match it" subtitle was a quiet
  // nudge — kept on the same line as the title now (separated by the
  // same vertical hairline as the other sections) so the rhythm
  // across all three boxes holds. Falls away once the user fills it.
  const filled = !!sentence
  return (
    <div
      className="px-4 pt-3 pb-3 border-t"
      style={{ borderColor: 'var(--border-subtle)', minHeight: ADDITIONAL_MIN_HEIGHT }}
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
        <span
          className="uppercase shrink-0"
          style={{
            color: 'var(--accent)',
            fontSize: 'var(--eyebrow-size)',
            fontWeight: 'var(--eyebrow-weight)',
            letterSpacing: 'var(--eyebrow-tracking)',
          }}
        >
          Additional Information
        </span>
        {!filled && (
          <>
            <span
              aria-hidden
              className="inline-block shrink-0"
              style={{
                width: '1px',
                height: '0.95em',
                background: 'var(--border-default)',
                transform: 'translateY(1px)',
              }}
            />
            <span
              className="text-[16px]"
              style={{
                fontFamily: 'var(--font-sans)',
                color: 'var(--ink-3)',
                letterSpacing: '0.01em',
                lineHeight: 1.45,
              }}
            >
              Be descriptive
            </span>
          </>
        )}
      </div>
      {filled && (
        <p
          className="text-[16px]"
          style={{
            fontFamily: 'var(--font-sans)',
            color: 'var(--ink-1)',
            fontWeight: 400,
            lineHeight: 1.5,
            letterSpacing: '0.01em',
            margin: 0,
          }}
        >
          {sentence}
        </p>
      )}
    </div>
  )
}
